import React from "react";
import { Box, HStack, Text } from "@chakra-ui/react";
import { alpha, useGovTheme } from "./useGovTheme";

/** Plain-words labels for every status governance shows. Unknown values show as-is, never hidden. */
const LABEL: Record<string, string> = {
  DRAFT: "Draft", ACTIVE: "Active", ARCHIVED: "Archived", OPEN: "Voting open", PASSED: "Passed", REJECTED: "Rejected",
  QUORUM_NOT_MET: "No quorum", CANCELLED: "Withdrawn", CLAIMED: "Claimed", SUBMITTED: "Proof in", ACCEPTED: "Accepted",
  PAID: "Paid", SUSPENDED: "Suspended", RESIGNED: "Left", PENDING: "Pending", READY: "Ready to decide", DECLINED: "Declined",
  WITHDRAWN: "Withdrawn", VIEWED: "Opened", APPLIED: "Applied", JOINED: "Joined", REVOKED: "Revoked", EXPIRED: "Expired",
  PROPOSED: "Proposed", ADOPTED: "Adopted", RETIRED: "Retired",
};
type Tone = "accent" | "success" | "warning" | "danger" | "neutral" | "purple";
const TONE: Record<string, Tone> = {
  ACTIVE: "success", OPEN: "accent", PASSED: "success", ACCEPTED: "success", PAID: "success", JOINED: "success", ADOPTED: "success", READY: "accent",
  REJECTED: "danger", DECLINED: "danger", REVOKED: "danger", EXPIRED: "danger", QUORUM_NOT_MET: "warning", SUSPENDED: "warning",
  CLAIMED: "purple", SUBMITTED: "purple", PENDING: "warning", VIEWED: "accent", APPLIED: "accent", PROPOSED: "accent",
};

/**
 * `label` and `tone` are overrides for modules whose statuses are not governance's.
 * The maps above stay governance's vocabulary; a second module supplies its own ONE
 * enum→words map and passes the result in, rather than colliding here (hosting's
 * READY means "Live", governance's means "Ready to decide").
 */
export function GovStatusPill({ status, label, tone: toneOverride }: { status: string; label?: string; tone?: Tone }) {
  const t = useGovTheme();
  const tone = toneOverride ?? TONE[status] ?? "neutral";
  const c = { accent: t.primary, success: t.success, warning: t.warning, danger: t.danger, neutral: t.neutral, purple: t.purple }[tone];
  return (
    <HStack as="span" spacing={1.5} px={3} py={1} borderRadius="full" bg={alpha(c, 0.12)} borderWidth="1px" borderColor={alpha(c, 0.28)} display="inline-flex" flexShrink={0}>
      <Box as="span" w="8px" h="8px" borderRadius="full" bg={c} boxShadow={status === "OPEN" || status === "ACTIVE" ? `0 0 0 3px ${alpha(c, 0.2)}` : undefined} />
      <Text as="span" fontSize="sm" fontWeight="semibold" color={c} whiteSpace="nowrap">{label ?? LABEL[status] ?? status}</Text>
    </HStack>
  );
}
